/**
 * GlobalView — useMediaQuery Hook
 *
 * Reactive CSS media-query listener.
 * Returns true when the viewport matches the given query string.
 *
 * Usage:
 *   const isMobile = useIsMobile();          // < 768px
 *   const isTablet = useMediaQuery('(max-width: 992px)');
 */

import { useState, useEffect } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Sync initial value (SSR → hydration case)
    setMatches(mql.matches);

    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/** Shorthand: true when viewport width ≤ 768px */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 768px)');
}
